import { Box, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Banner } from 'components/banner';
import { InfoCircle } from 'components/icons/info-circle';
import type {
  EducationCode,
  ProfessionCode,
  SuitabilityAnswers,
} from './answers';
import { MultiSelectQuestion } from 'src/suitability/engine/components/multi-select-question';
import { RadioQuestion } from 'src/suitability/engine/components/radio-question';
import { buildVfOptions, useWioCapitalContent } from './content';

interface StepProps {
  answers: SuitabilityAnswers;
  onUpdate: (patch: Partial<SuitabilityAnswers>) => void;
}

export const StepBlock1 = ({ answers, onUpdate }: StepProps) => {
  const { t } = useTranslation();
  const content = useWioCapitalContent();
  const step = content.step('block1');
  const sections = step.sections ?? [];
  const section = (key: string) => sections.find((s) => s.key === key);
  const p3 = content.p3();
  const p4 = content.p4();
  const p5 = content.p5();

  const basic = section('basic');
  const complex = section('complex');
  const experience = section('experience');

  // Direct bypassa P2–P5: el motor marca todas las familias convenientes.
  const isBypass = answers.p1 === 'Direct';

  return (
    <Box>
      <Typography variant="textXl" sx={{ fontWeight: 700, mb: 1 }}>
        {step.title}
      </Typography>
      <Typography
        variant="textSm"
        sx={{ color: 'text.secondary', mb: 3, display: 'block' }}
      >
        {step.intro}
      </Typography>

      <RadioQuestion
        question={content.radio('p1').prompt}
        options={content.radio('p1').options}
        value={answers.p1}
        onChange={(v) => onUpdate({ p1: v as ProfessionCode })}
        required
      />

      {isBypass && (
        <Box sx={{ mb: 3 }}>
          <Banner
            color="grayBlue"
            Icon={<InfoCircle />}
            message={t('wioCapital.professionalBypass')}
          />
        </Box>
      )}

      {!isBypass && answers.p1 && (
        <>
          <RadioQuestion
            question={content.radio('p2').prompt}
            options={content.radio('p2').options}
            value={answers.p2}
            onChange={(v) => onUpdate({ p2: v as EducationCode })}
            required
          />

          <Typography variant="textLg" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            {basic?.title}
          </Typography>
          <Typography
            variant="textSm"
            sx={{ color: 'text.secondary', mb: 2, display: 'block' }}
          >
            {basic?.intro}
          </Typography>
          {p3.questions.map((question) => (
            <RadioQuestion
              key={question.answerKey}
              question={question.prompt}
              options={question.options}
              value={
                answers[question.answerKey as keyof SuitabilityAnswers] as
                  | string
                  | undefined
              }
              onChange={(v) =>
                onUpdate({ [question.answerKey]: v } as Partial<SuitabilityAnswers>)
              }
              required
            />
          ))}

          <Typography variant="textLg" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            {complex?.title}
          </Typography>
          <Typography
            variant="textSm"
            sx={{ color: 'text.secondary', mb: 2, display: 'block' }}
          >
            {complex?.intro}
          </Typography>
          {p4.families.map((family) => (
            <Paper
              key={family.familyKey}
              variant="outlined"
              sx={{ mb: 4, p: 4, pb: 1, borderRadius: 1.5 }}
            >
              <Typography variant="textSm" sx={{ fontWeight: 600, mb: 2 }}>
                {family.familyLabel}
              </Typography>
              {family.statements.map((statement) => (
                <RadioQuestion
                  key={statement.id}
                  question={statement.label}
                  options={buildVfOptions(statement.id, p4.vfLabels)}
                  value={
                    answers[statement.answerKey as keyof SuitabilityAnswers] as
                      | string
                      | undefined
                  }
                  onChange={(v) =>
                    onUpdate({
                      [statement.answerKey]: v,
                    } as Partial<SuitabilityAnswers>)
                  }
                  required
                />
              ))}
            </Paper>
          ))}

          <Typography variant="textLg" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            {experience?.title}
          </Typography>
          <Typography
            variant="textSm"
            sx={{ color: 'text.secondary', mb: 2, display: 'block' }}
          >
            {experience?.intro}
          </Typography>
          <MultiSelectQuestion
            options={p5.families.map((family) => ({
              id: family.id,
              label: family.label,
              badge: family.isComplex ? p5.complexBadge : undefined,
            }))}
            value={answers.p5}
            onChange={(p5Answers) => onUpdate({ p5: p5Answers })}
          />
        </>
      )}
    </Box>
  );
};
